import { format } from "date-fns";
import { validateDate } from "../../app/common";
import { SearchFilter } from "../components/functional/FilterSearchField/FilterSearchField";
import { NameIdOption } from "../types/commonTypes";
import { isoFormat } from "./formatDateTime";
import { AutocompleteType } from "../types/autoComplete";

export const getFiltersFromLocalStorage = (key: string): SearchFilter[] => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return [];
    return JSON.parse(value);
  } catch {
    // Not found
    return [];
  }
};

// Compare a list of initial filters to those in local storage
// - If a key is missing from local storage filters, add it with default/empty value
export const mergeInitialAndStoredFilters = (
  initialFilters: SearchFilter[],
  storedFilters: SearchFilter[],
): SearchFilter[] => {
  const mergedFilters = [...storedFilters];

  initialFilters.forEach((initialFilter) => {
    const exists = storedFilters.some(
      (storedFilter) => storedFilter.name === initialFilter.name,
    );
    if (!exists) {
      mergedFilters.push(initialFilter);
    }
  });

  return mergedFilters;
};

export function toStringArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [String(value)];
}

export function isSearchTermsFilter(filter: SearchFilter): boolean {
  return filter.name === "searchTerms";
}

// Returns display value and tooltip for chip display for arrays of { name }.
export function makeMultiSelectDisplay<T extends { name: string }>(
  arr: T[],
): { displayValue: string; tooltip?: string } {
  if (arr.length === 0) return { displayValue: "" };
  if (arr.length === 1) return { displayValue: arr[0].name };
  return {
    displayValue: `${arr[0].name} +${arr.length - 1}`,
    tooltip: arr.map((item) => item.name).join(", "),
  };
}

// Returns id+name array for lookupValues property
export function makeLookupValues<T extends NameIdOption>(arr: T[]) {
  return arr.map(({ id, name }) => ({ id, name }));
}

// Converts array of IDs to NameIdOption array using lookupValues and optional fallback options
export function idsToNameIdOptions(
  ids: string[],
  lookupValues?: NameIdOption[],
  fallbackOptions?: NameIdOption[],
): NameIdOption[] {
  return ids.map((id) => {
    const lookup = lookupValues?.find((v) => v.id === id);
    const fallback = fallbackOptions?.find((opt) => opt.id === id);
    return {
      id,
      name: lookup?.name || fallback?.name || id,
    };
  });
}

// Converts array of IDs to LocalizedSkill array using lookupValues
// Note: fieldOfWorkId is set to empty string as default
export function idsToLocalizedSkills(
  ids: string[],
  lookupValues?: NameIdOption[],
): Array<{ id: string; name: string; fieldOfWorkId: string }> {
  return idsToNameIdOptions(ids, lookupValues).map((option) => ({
    ...option,
    fieldOfWorkId: "",
  }));
}

export function parseAutocomplete(
  filter: SearchFilter | undefined,
): AutocompleteType[] {
  if (!filter) return [];
  let storedValue = filter.value;
  if (storedValue && !Array.isArray(storedValue)) storedValue = [storedValue];
  if (!storedValue?.length) return [];
  return storedValue.map((id: string) => ({
    id,
    name: filter.lookupValues?.find((lv) => lv.id === id)?.name || "",
  }));
}

// Get value for epoch/date filters - if valid date, convert to epoch time, otherwise return empty
export function dateToTimeOrEmpty(
  date: Date | null | undefined,
): number | string {
  return date && validateDate(date, isoFormat) ? date.getTime() : "";
}

export function dateToFormatOrEmpty(
  date: Date | null | undefined,
  formatStr: string,
): string {
  return date && validateDate(date, isoFormat) ? format(date, formatStr) : "";
}

// Replaces commas to avoid conflicts with comma as a separator in query parameters
// Note: needs to be parsed back in back end
export const replaceCommasFromValue = (value: string) => {
  return value.replaceAll(",", "␞");
};

export const convertYesNoToBooleanParam = (value?: string) => {
  return value ? (value === "yes" ? "true" : "false") : "";
};

export const convertBooleanToYesNoValue = (value?: string) => {
  return value ? (value === "true" ? "yes" : "no") : "";
};
